import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber, formatDateTime } from '@/utils/format';
import type { LPAccountDto, LPAccountTotalsDto } from '@/api/types/lpAccounts';

const n = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));
const pct = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? 'N/A' : `${formatNumber(v, 2)}%`);
const dt = (v: string | null | undefined) => formatDateTime(v);

export function LpAccountsGrid({
  accounts,
  totals,
  onOpenSlippage,
  onOpenLogin,
  toolbarStart,
}: {
  accounts: LPAccountDto[];
  totals: LPAccountTotalsDto | null;
  onOpenSlippage: (platformId: number, serverName: string) => void;
  onOpenLogin: (login: number, platformId: number) => void;
  // Rendered in the grid toolbar, left of the Excel + eye buttons (Margin Thresholds).
  toolbarStart?: ReactNode;
}) {
  // Breach-first (orderFixed breachLevel desc in legacy).
  const rows = useMemo(() => [...accounts].sort((a, b) => (b.breachLevel ?? 0) - (a.breachLevel ?? 0)), [accounts]);

  const marginLevelCell = (v: number) => {
    if (v == null || Number.isNaN(v)) return <span className="text-muted">N/A</span>;
    const cls = v < 100 ? 'text-danger' : v < 200 ? 'text-warning' : 'text-primary';
    return <span className={`fw-semibold ${cls}`}>{formatNumber(v, 2)}%</span>;
  };

  const columns: DataGridColumn<LPAccountDto>[] = [
    {
      key: 'serverName',
      title: 'Server Name',
      className: 'text-start',
      value: (r) => r.serverName,
      cell: (r) => (
        <span className="rd-instrument-cell">
          <span>{r.serverName}</span>
          <button type="button" className="rd-info-btn" title="Slippage summary" onClick={() => onOpenSlippage(r.platformId, r.serverName)}>
            <i className="fe fe-info" />
          </button>
        </span>
      ),
    },
    {
      key: 'login',
      title: 'Login',
      className: 'text-start',
      sortable: false,
      value: (r) => r.login,
      cell: (r) =>
        r.platformId ? (
          <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); onOpenLogin(Number(r.login), r.platformId); }}>{r.login}</a>
        ) : (
          <span className="fw-semibold">{r.login}</span>
        ),
    },
    { key: 'accountCurrency', title: 'Acc Currency', className: 'text-start', value: (r) => r.accountCurrency },
    { key: 'balanceRc', title: 'Balance', className: 'text-end', value: (r) => r.balanceRc, cell: (r) => n(r.balanceRc) },
    { key: 'creditRc', title: 'Credit', className: 'text-end', value: (r) => r.creditRc, cell: (r) => n(r.creditRc) },
    { key: 'floatingRc', title: 'Floating PnL', className: 'text-end', value: (r) => r.floatingRc, cell: (r) => n(r.floatingRc) },
    { key: 'equityRc', title: 'Equity', className: 'text-end', value: (r) => r.equityRc, cell: (r) => n(r.equityRc) },
    { key: 'revenueShare', title: 'Revenue Share', className: 'text-end', value: (r) => r.revenueShare ?? null, cell: (r) => n(r.revenueShare) },
    { key: 'marginUsedRc', title: 'Margin Used', className: 'text-end', value: (r) => r.marginUsedRc, cell: (r) => n(r.marginUsedRc) },
    { key: 'freeMarginRc', title: 'Free Margin', className: 'text-end', value: (r) => r.freeMarginRc, cell: (r) => n(r.freeMarginRc) },
    { key: 'marginLevel', title: 'Margin Level', className: 'text-end', value: (r) => r.marginLevel, cell: (r) => marginLevelCell(r.marginLevel) },
    { key: 'riskyVol', title: 'Risky Vol', className: 'text-end', value: (r) => r.riskyVol, cell: (r) => n(r.riskyVol) },
    { key: 'platformName', title: 'Connector', className: 'text-start', value: (r) => r.platformName },
    { key: 'serverTime', title: 'Server Time', className: 'text-start', value: (r) => r.serverTime ?? '', cell: (r) => dt(r.serverTime) },
  ];

  // Totals row at the TOP (tfoot display:table-header-group). Keyed by column so the
  // column-visibility toggle drops the matching totals cell too and stays aligned
  // (legacy DataTables footer behaviour).
  const footerCells: Record<string, ReactNode> = totals
    ? {
        serverName: 'Total',
        balanceRc: n(totals.totalBalanceRc),
        creditRc: n(totals.totalCreditRc),
        floatingRc: n(totals.totalFloatingRc),
        equityRc: n(totals.totalEquityRc),
        marginUsedRc: n(totals.totalMarginUsedRc),
        freeMarginRc: n(totals.totalFreeMarginRc),
        marginLevel: pct(totals.averageMarginLevel),
        riskyVol: n(totals.totalRiskyVol),
      }
    : {};

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => `${r.platformId}:${r.login}`}
      pageSize={5}
      card={false}
      toolbarStart={toolbarStart}
      includeExcel
      excelFileName="lp-accounts"
      footerCells={totals ? footerCells : undefined}
      rowClassName={(r) => (r.breachLevel > 0 ? 'threshold-breach' : undefined)}
      emptyMessage="Waiting for live LP accounts from connected terminals."
    />
  );
}
